import React, { useCallback, useState } from 'react';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { SetupTitle } from '../components/setup-page-title';
import OnboardingSetupFooterButtons from '../components/setup-footer-buttons';
import { FeatureInterestQuestion } from '../components/feature-interest-question';
import { LogService } from '../../../services/log-service';
import { useProgressiveDisclosureSegmentTracking } from '../hooks/use-progressive-disclosure-segment-tracking';

import { useSetupContext } from '../context';

import { FEATURE_INTEREST, ONBOARDING_SETUP_EVENTS } from '../constants';
import messages from '../messages';
import { emptyArray } from 'ui-components/default-props/empty';

export const FeatureInterestForm = () => {
  const { onContinue } = useSetupContext();
  const [isSubmitting, setIsSubmitting] = useAsyncSafeState(false);
  const [featuresOfInterest, setFeaturesOfInterest] = useState(emptyArray);
  const trackProgressiveDisclosure = useProgressiveDisclosureSegmentTracking(
    ONBOARDING_SETUP_EVENTS.PROGRESSIVE_DISCLOSURE_QUESTION_ANSWERED,
  );

  const onClickSkip = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onContinue({ featuresOfInterest: emptyArray });
      trackProgressiveDisclosure({
        question: FEATURE_INTEREST,
        featuresOfInterest: emptyArray,
      });
    } catch (error) {
      LogService.logError(new Error('Error saving feature interests', { cause: error }));
    }
    setIsSubmitting(false);
  }, [onContinue, setIsSubmitting, trackProgressiveDisclosure]);

  const onClickContinue = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onContinue({ featuresOfInterest });
      trackProgressiveDisclosure({
        question: FEATURE_INTEREST,
        featuresOfInterest,
      });
    } catch (error) {
      LogService.logError(new Error('Error saving feature interests', { cause: error }), { featuresOfInterest });
    }
    setIsSubmitting(false);
  }, [setIsSubmitting, onContinue, featuresOfInterest, trackProgressiveDisclosure]);

  const onSelect = useCallback((value) => {
    setFeaturesOfInterest((currentFeatures) => {
      if (currentFeatures.includes(value)) {
        return currentFeatures.filter((item) => item !== value);
      }
      return [...currentFeatures, value];
    });
  }, []);

  return (
    <>
      <SetupTitle subtitleDescriptor={messages.featureInterestQuestionSubtitle} titleDescriptor={messages.featureInterestQuestionTitle} />
      <FeatureInterestQuestion onSelect={onSelect} selectedInterests={featuresOfInterest} />
      <OnboardingSetupFooterButtons
        disableContinueButton={!featuresOfInterest.length || isSubmitting}
        disableSkipButton={isSubmitting}
        hideBackButton
        onContinue={onClickContinue}
        onSkip={onClickSkip}
        skipMessageDescriptor={messages.justLookingAround}
      />
    </>
  );
};
